import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gravatar } from 'components/external/gravatar';
import { useTranslate } from 'i18n-calypso';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@wordpress/components';
import { chevronDown, Icon } from '@wordpress/icons';
import clsx from 'clsx';

import useMeQuery from 'data/me/use-me-query';
import useOutsideClickCallback from 'lib/use-outside-click-callback';

import './style.scss';

interface ProfileDropdownProps {
	/** Avatar only, menu opening downward and on hover — for the redesigned header. */
	compact?: boolean;
}

// Grace period before a hover-opened menu closes, so the pointer can travel into it.
const HOVER_CLOSE_DELAY = 150;

type DropdownMenuProps = {
	isExpanded: boolean;
	setMenuExpanded: ( isExpanded: boolean ) => void;
};

const DropdownMenu = ( { isExpanded }: DropdownMenuProps ) => {
	const translate = useTranslate();
	const navigate = useNavigate();
	const onSignOut = () => navigate( '/logout' );

	return (
		<ul
			className="absolute left-0 right-0 bottom-0 list-none p-2 border border-[var(--hpp-color-accent-0)] bg-[var(--hpp-color-surface)] rounded leading-normal"
			style={ { boxShadow: '0 4px 6px rgba(var(--hpp-color-accent-100-rgb),.1),0 1px 2px rgba(var(--hpp-color-accent-100-rgb),.1)' } }
			hidden={ ! isExpanded }
		>
			<li className="m-0 border border-transparent">
				<button
					className={clsx(
						'block p-2 w-full',
						'border-[var(--hpp-color-accent-5)] rounded',
						'text-left text-sm font-medium text-[var(--hpp-color-accent-80)]',
						'hover:bg-[var(--wp-components-color-accent)] hover:fill-[var(--hpp-color-text-inverted)] hover:text-[var(--hpp-color-text-inverted)]',
					)}
					onClick={ onSignOut }
				>
					{ translate( 'Sign out' ) }
				</button>
			</li>
		</ul>
	);
};

const ProfileDropdown = ( { compact = false }: ProfileDropdownProps ) => {
	const translate = useTranslate();
	const [ isMenuExpanded, setMenuExpanded ] = useState( false );
	const { data: user, isSuccess } = useMeQuery();

	const onToggleMenu = useCallback( () => {
		setMenuExpanded( ( current ) => ! current );
	}, [] );
	const onCloseMenu = useCallback( () => {
		if ( ! isMenuExpanded ) {
			return;
		}

		setMenuExpanded( false );
	}, [ isMenuExpanded ] );

	const dropdownRef = useRef( null );
	useOutsideClickCallback( dropdownRef, onCloseMenu );

	// Compact mode opens on hover — only where a hover exists. On touch, a tap fires mouseenter and
	// then click, which would open and immediately toggle the menu closed again.
	const canHover = useMemo(
		() => typeof window !== 'undefined' && !! window.matchMedia?.( '(hover: hover)' ).matches,
		[],
	);
	const opensOnHover = compact && canHover;
	const closeTimer = useRef< number | undefined >( undefined );
	const onMouseEnter = useCallback( () => {
		if ( ! opensOnHover ) {
			return;
		}
		window.clearTimeout( closeTimer.current );
		setMenuExpanded( true );
	}, [ opensOnHover ] );
	const onMouseLeave = useCallback( () => {
		if ( ! opensOnHover ) {
			return;
		}
		window.clearTimeout( closeTimer.current );
		closeTimer.current = window.setTimeout( () => setMenuExpanded( false ), HOVER_CLOSE_DELAY );
	}, [ opensOnHover ] );
	useEffect( () => () => window.clearTimeout( closeTimer.current ), [] );

	if ( ! isSuccess ) {
		return (
			<div className="h-12 pl-2 py-2 flex items-center">
				<Spinner style={ { margin: 0 } } />
			</div>
		);
	}

	return (
		<div
			ref={ dropdownRef }
			className={ clsx( 'profile-dropdown relative py-2', { 'profile-dropdown--compact': compact } ) }
			onMouseEnter={ onMouseEnter }
			onMouseLeave={ onMouseLeave }
		>
			<button
				aria-expanded={ isMenuExpanded }
				aria-controls="menu-list"
				aria-label={ compact ? translate( 'My Profile', { textOnly: true } ) : undefined }
				className="flex items-center gap-3"
				onClick={ onToggleMenu }
			>
				<Gravatar
					user={ {
						avatar_URL: user.avatar_URL,
						name: user.display_name,
					} }
					size={ 32 }
					alt={ translate( 'My Profile', { textOnly: true } ) }
				/>
				{ ! compact && (
					<span className="flex items-center gap-1 text-sm font-medium">
						{ user?.display_name }
						<Icon icon={ chevronDown } />
					</span>
				) }
			</button>
			<DropdownMenu isExpanded={ isMenuExpanded } setMenuExpanded={ setMenuExpanded } />
		</div>
	);
};

export default ProfileDropdown;
